import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { getApiErrorMessage } from "@/lib/utils_pharma";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Search, Users, Receipt, FileText } from "lucide-react";
import PatientSalesReportDialog from "@/components/PatientSalesReportDialog";

const empty = {
  name: "",
  phone: "",
  email: "",
  age: "",
  gender: "",
  address: "",
  medical_history: ""
};

export default function Patients() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reportPatientId, setReportPatientId] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/patients", { params: { q } });
      setItems(data);
    } catch (e) {
      toast.error("Failed to load patients");
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const openNew = () => {
    setForm(empty);
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (p) => {
    setForm({
      ...empty,
      ...p,
      age: p.age !== null && p.age !== undefined ? String(p.age) : ""
    });
    setEditId(p.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const payload = {
      ...form,
      age: form.age ? Number(form.age) : null,
    };
    try {
      if (editId) {
        await api.put(`/patients/${editId}`, payload);
        toast.success("Patient updated");
      } else {
        await api.post("/patients", payload);
        toast.success("Patient added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/patients/${deleteTarget.id}`);
      toast.success("Patient deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Failed to delete patient");
    }
  };

  return (
    <div className="space-y-6" data-testid="patients-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Registry</div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: "Chivo" }}>Patients</h1>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="patients-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search patients…"
              className="pl-9 w-64 bg-white"
            />
          </div>
          <Button onClick={openNew} data-testid="add-patient-btn">
            <Plus size={16} className="mr-1" /> Add Patient
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Age / Gender</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Medical History</TableHead>
              <TableHead className="text-center">Total Bills</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                  <Users className="mx-auto mb-2 opacity-40" size={24} />
                  No patients found. Add your first patient record.
                </TableCell>
              </TableRow>
            )}
            {items.map((p) => (
              <TableRow
                key={p.id}
                className="data-row cursor-pointer hover:bg-muted/40 transition-colors"
                onClick={() => {
                  setReportPatientId(p.id);
                  setReportModalOpen(true);
                }}
                data-testid={`patient-row-${p.id}`}
              >
                <TableCell className="font-medium text-primary hover:underline">{p.name}</TableCell>
                <TableCell className="font-mono text-xs">{p.phone || "—"}</TableCell>
                <TableCell className="text-xs">{p.email || "—"}</TableCell>
                <TableCell className="text-xs">
                  {p.age ? `${p.age} yrs` : "—"} / {p.gender || "—"}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground truncate max-w-xs">{p.address || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground truncate max-w-xs">{p.medical_history || "—"}</TableCell>
                <TableCell className="text-center font-mono text-xs tabular-nums" data-testid={`patient-bills-${p.id}`}>
                  {typeof p.total_bills === "number" ? p.total_bills : (parseInt(p.total_bills, 10) || 0)}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => {
                      setReportPatientId(p.id);
                      setReportModalOpen(true);
                    }}
                    data-testid={`report-${p.id}`}
                    title="View Patient Sales Report"
                    className="p-1 hover:bg-muted rounded text-primary"
                  >
                    <Receipt size={14} />
                  </button>
                  <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} title="Edit Patient" className="p-1 hover:bg-muted rounded ml-1">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => setDeleteTarget(p)} data-testid={`delete-${p.id}`} title="Delete Patient" className="p-1 hover:bg-muted rounded text-destructive ml-1">
                    <Trash2 size={14} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="patient-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Patient" : "Add Patient"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Name *</Label>
              <Input
                data-testid="patient-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Patient's Full Name"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input
                data-testid="patient-phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone Number"
              />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email Address"
                type="email"
              />
            </div>
            <div>
              <Label className="text-xs">Age (Years)</Label>
              <Input
                value={form.age}
                onChange={(e) => setForm({ ...form, age: e.target.value })}
                placeholder="Age"
                type="number"
                min="0"
                max="150"
              />
            </div>
            <div>
              <Label className="text-xs">Gender</Label>
              <Select
                value={form.gender}
                onValueChange={(val) => setForm({ ...form, gender: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Resident Address"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Medical History</Label>
              <Input
                value={form.medical_history}
                onChange={(e) => setForm({ ...form, medical_history: e.target.value })}
                placeholder="Allergies, chronic diseases, or history notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-patient">
              {editId ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Delete Patient</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">
            Delete <span className="font-semibold text-foreground">{deleteTarget?.name}</span>? This action cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={remove}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PATIENT SALES REPORT MODAL */}
      <PatientSalesReportDialog
        open={reportModalOpen}
        onOpenChange={setReportModalOpen}
        patientId={reportPatientId}
      />
    </div>
  );
}
